import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { INavigateAway } from '../../shared/guards/NavigationGuard';
import { SubscriptionList } from '../../shared/models/asp.types';
import { Departments } from '../../shared/enums/departments.enum';
import { HoursOfOperation, IDuration, ITimeZone } from '../../shared/models/settings.model';
import { AlertService } from '../../shared/services/alert.service';
import { exitConfig, environment } from '../../shared/services/util.service';
import { SettingsService } from '../../shared/services/settings.service';
import * as util from '../../shared/services/util.service';
import * as fromActions from '../../store/settings/settings.actions';
import { SettingsState } from '../../store/settings/settings.reducer';
import * as fromSelector from '../../store/settings/settings.selectors';

@Component({
  selector: 'app-parts-general-settings',
  templateUrl: './parts-general-settings.component.html',
  styleUrls: ['./parts-general-settings.component.scss']
})
export class PartsGeneralSettingsComponent implements OnInit, INavigateAway, OnDestroy {
  department: string = Departments.PARTS;
  settingsForm!: UntypedFormGroup;
  settings$!: Observable<any>;
  timezones$!: Observable<ITimeZone[]>;
  scheduleValues: string[] = ['1 Day', '2 Days', '3 Days', '5 Days', '7 Days', '14 Days', '21 Days'];
  amenities: string[] = [];
  hasAmenities = false;
  hoursOfOperation: HoursOfOperation[] = [];
  timeDuration: IDuration[] = [];
  curOffTime: IDuration[] = [];
  subs: SubscriptionList = {};
  syncInProgress = false;
  sourceSettings!: any;
  saveInProgress = false;

  notificationList = [
    { name: 'syncFailure', header: 'generalSettings.failedNotifications' },
    { name: 'appointment', header: 'generalSettings.appointmentNotifications' }
  ];

  customerNotificationEnabled = false;
  reminderNotificationEnabled = false;
  portalSwitches = {
    enableParts: false,
    enableAmenities: true
  };

  tooltip: any;

  constructor(
    private readonly settingsState: Store<SettingsState>,
    private readonly fb: UntypedFormBuilder,
    private readonly settingsService: SettingsService,
    private readonly alertService: AlertService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog
  ) {
    this.setUpStore();
    this.initiateForm();
    this.initializeSubscriptions();
    this.tooltip = {
      time: this.translate.instant('generalSettings.toUpdateTheOperationalHoursGoToTheToyotaDISsystem'),
      portal: this.translate.instant('generalSettings.manageSettingsOntheCustomerOnlinePortal'),
      customerNotification: this.translate.instant('generalSettings.enableToAllowUsersToReviceEmailNotifications'),
      notifications: this.translate.instant('generalSettings.automatedEmailsWillBeSentForTheTriggerPointsYouSetHere')
    };
  }

  ngOnInit(): void {
    this.curOffTime = this.settingsService.getCutOffTime();
    this.timeDuration = this.settingsService.getTimeDuration(this.department);
  }

  canExit(): Observable<boolean> {
    const result = new Subject<boolean>();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, exitConfig);
    this.subs.dialog = dialogRef.afterClosed().subscribe(event => {
      if (event?.action === 'confirm') {
        this.saveChanges();
      }
      result.next(true);
      result.complete();
    });
    return result.asObservable();
  }

  formsNotEdited() {
    return !this.settingsForm?.dirty;
  }

  setUpStore() {
    const payload = { department: this.department };
    this.settingsState.dispatch(new fromActions.LoadSettings(payload));
    this.settingsState.dispatch(new fromActions.LoadTimezones());
    this.settings$ = this.settingsState.select(fromSelector.selectSettings);
    this.timezones$ = this.settingsState.select(fromSelector.selectTimezones);
  }

  initializeSubscriptions() {
    this.subs.settingsLoaded = this.settingsState.select(fromSelector.selectSettingsLoaded).subscribe(loaded => {
      if (loaded) {
        this.saveInProgress = false;
      }
    });
    this.subs.settings = combineLatest([this.settings$, this.timezones$]).subscribe(([settings]) => {
      if (!settings) {
        return;
      }

      this.sourceSettings = JSON.parse(JSON.stringify(settings));
      this.hoursOfOperation = settings.time?.hoursOfOperation || [];
      this.amenities = this.amenities?.length === 0 ? settings.portal?.amenities : this.amenities ;
      this.hasAmenities = !!this.amenities?.length;

      this.settingsForm.patchValue({
        time: {
          slotDuration: settings.time?.slotDuration,
          cutOffTime: settings.time?.cutOffTime,
          timeZone: settings.time?.timeZone
        },
        portal: {
          enableParts: this.getPartsPortalEnabled(settings),
          enableAmenities: this.getAmenitiesEnabled(settings),
          partsBannerTitleText: settings.portal?.partsBannerTitleText || '',
          partsBannerBodyText: settings.portal?.partsBannerBodyText || ''
        },
        customerNotificationEnabled: settings.customerNotificationEnabled,
        reminderNotificationEnabled: settings.reminderNotificationEnabled,
        reminderNotificationSchedule: settings.reminderNotificationSchedule || []
      });

      if (!this.settingsForm.get('time')?.get('timeZone')?.value) {
        this.setupTimezoneValidation();
      }

      this.initializeNotification(settings.notification || {});
      this.setSwitchValues(settings);
      this.settingsForm.markAsPristine();
    });
  }

  initiateForm() {
    this.settingsForm = this.fb.group({
      notification: this.fb.group({
        syncFailure: this.fb.group({
          enable: [true],
          distributionList: this.fb.array([])
        }),
        appointment: this.fb.group({
          enable: [false],
          distributionList: this.fb.array([])
        })
      }),
      time: this.fb.group({
        slotDuration: [''],
        cutOffTime: [''],
        timeZone: ['', Validators.required]
      }),
      portal: this.fb.group({
        enableParts: [false],
        enableAmenities: [true],
        partsBannerTitleText: [''],
        partsBannerBodyText: ['']
      }),
      customerNotificationEnabled: [false],
      reminderNotificationEnabled: [false],
      reminderNotificationSchedule: [[]]
    });
  }

  setupTimezoneValidation() {
    this.settingsForm.get('time')?.get('timeZone')?.markAsTouched();
    this.settingsForm.get('time')?.get('timeZone')?.updateValueAndValidity();
  }

  setReminderNotifValidation() {
    const reminderNotifField = this.settingsForm.get('reminderNotificationEnabled') as UntypedFormControl;
    if (this.settingsForm.get('customerNotificationEnabled')?.value) {
      reminderNotifField.enable();
    } else {
      reminderNotifField.patchValue(false);
      reminderNotifField.disable();
    }
    this.setReminderScheduleValidation();
  }

  setReminderScheduleValidation() {
    const reminderScheduleField = this.settingsForm.get('reminderNotificationSchedule') as UntypedFormControl;
    const isEnabled = this.settingsForm.get('reminderNotificationEnabled')?.value
      && this.settingsForm.get('customerNotificationEnabled')?.value;

    if (isEnabled) {
      reminderScheduleField.setValidators([Validators.required]);
      reminderScheduleField.enable();
      reminderScheduleField.markAsTouched();
    } else {
      reminderScheduleField.clearValidators();
      reminderScheduleField.patchValue([]);
      reminderScheduleField.disable();
    }
    reminderScheduleField.updateValueAndValidity();
  }

  initializeNotification(notification: any) {
    this.notificationList.forEach(type => {
      this.settingsForm.get('notification')?.get(type.name)?.get('enable')?.patchValue(!!notification[type.name]?.enable);
      this.distributionList(type.name).clear();
      const distribution = notification[type.name]?.distributionList || [];
      distribution.forEach((email: string) => this.addDistributionList(type.name, email));
    });
  }

  setSwitchValues(settings: any) {
    this.portalSwitches.enableParts = this.getPartsPortalEnabled(settings);
    this.portalSwitches.enableAmenities = this.getAmenitiesEnabled(settings);
    this.customerNotificationEnabled = !!settings.customerNotificationEnabled;
    this.reminderNotificationEnabled = !!settings.reminderNotificationEnabled;
    this.setReminderNotifValidation();
  }

  getAmenitiesEnabled(settings: any): boolean {
    if (!this.hasAmenities) {
      return false;
    }
    const portalSettings = settings?.portal || {};
    const objectWithHasOwn = Object as ObjectConstructor & {
      hasOwn(object: object, property: PropertyKey): boolean;
    };
    return objectWithHasOwn.hasOwn(portalSettings, 'enableAmenities')
      ? !!portalSettings.enableAmenities
      : true;
  }

  getPartsPortalEnabled(settings: any): boolean {
    const portalSettings = settings?.portal || {};
    const objectWithHasOwn = Object as ObjectConstructor & {
      hasOwn(object: object, property: PropertyKey): boolean;
    };
    return objectWithHasOwn.hasOwn(portalSettings, 'enableParts')
      ? !!portalSettings.enableParts
      : false;
  }

  updateSwitchValues(val: boolean, property: string) {
    switch (property) {
      case 'enableParts':
        this.portalSwitches.enableParts = val;
        this.settingsForm.patchValue({
          portal: {
            enableParts: val
          }
        });
        break;

      case 'enableAmenities':
        this.portalSwitches.enableAmenities = val;
        this.settingsForm.patchValue({
          portal: {
            enableAmenities: val
          }
        });
        break;

      case 'syncFailure':
      case 'appointment':
        this.settingsForm.patchValue({
          notification: {
            [property]: {
              enable: val
            }
          }
        });
        break;

      case 'customerNotificationEnabled':
        this.settingsForm.get('customerNotificationEnabled')?.patchValue(val);
        if (!val) {
          this.settingsForm.get('reminderNotificationEnabled')?.patchValue(false);
          this.reminderNotificationEnabled = false;
        }
        this.customerNotificationEnabled = val;
        this.setReminderNotifValidation();
        break;

      case 'reminderNotificationEnabled':
        this.settingsForm.get('reminderNotificationEnabled')?.patchValue(val);
        this.reminderNotificationEnabled = val;
        this.setReminderScheduleValidation();
        break;

      default:
        break;
    }

    this.settingsForm.markAsDirty();
  }

  distributionList(type: string) {
    return this.settingsForm.get('notification')?.get(type)?.get('distributionList') as UntypedFormArray;
  }

  isEnable(type: string) {
    return this.settingsForm.get('notification')?.get(type)?.get('enable')?.value;
  }

  addDistributionList(type: string, value = '') {
    const control = this.fb.control(value, [Validators.required, Validators.email, this.noWhitespaceValidator]);
    this.distributionList(type).push(control);
    this.settingsForm.markAsDirty();
  }

  removeDistributionList(type: string, index: number) {
    this.distributionList(type).removeAt(index);
    this.settingsForm.markAsDirty();
  }

  noWhitespaceValidator(control: any) {
    const isWhitespace = (control.value || '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  saveChanges() {
    if (!this.settingsForm.valid) {
      this.alertService.showAlert('otherTexts.pleaseUpdateInvalidFields');
      return;
    }

    const formData = this.settingsForm.getRawValue();
    const updatedSettings = JSON.parse(JSON.stringify(this.sourceSettings || {}));

    updatedSettings.time = {
      slotDuration: formData.time.slotDuration,
      cutOffTime: formData.time.cutOffTime,
      timeZone: formData.time.timeZone,
      hoursOfOperation: this.hoursOfOperation
    };

    updatedSettings.portal = {
      ...updatedSettings.portal,
      ...formData.portal
    };

    updatedSettings.notification = {
      ...updatedSettings.notification,
      syncFailure: formData.notification.syncFailure,
      appointment: formData.notification.appointment
    };

    updatedSettings.customerNotificationEnabled = formData.customerNotificationEnabled;
    updatedSettings.reminderNotificationEnabled = formData.reminderNotificationEnabled;
    updatedSettings.reminderNotificationSchedule = formData.reminderNotificationSchedule;

    this.settingsState.dispatch(new fromActions.UpdateSettings({
      department: this.department,
      settings: updatedSettings
    }));
    this.saveInProgress = true;

    this.settingsForm.markAsPristine();
  }

  cancel() {
    this.settingsForm.reset();
    if (this.sourceSettings) {
      this.sourceSettings = JSON.parse(JSON.stringify(this.sourceSettings));
      this.hoursOfOperation = this.sourceSettings.time?.hoursOfOperation || [];
      this.amenities = this.sourceSettings.portal?.amenities || [];
      this.settingsForm.patchValue({
        time: {
          slotDuration: this.sourceSettings.time?.slotDuration,
          cutOffTime: this.sourceSettings.time?.cutOffTime,
          timeZone: this.sourceSettings.time?.timeZone
        },
        portal: {
          enableParts: this.getPartsPortalEnabled(this.sourceSettings),
          enableAmenities: this.getAmenitiesEnabled(this.sourceSettings),
          partsBannerTitleText: this.sourceSettings.portal?.partsBannerTitleText || '',
          partsBannerBodyText: this.sourceSettings.portal?.partsBannerBodyText || ''
        },
        customerNotificationEnabled: this.sourceSettings.customerNotificationEnabled,
        reminderNotificationEnabled: this.sourceSettings.reminderNotificationEnabled,
        reminderNotificationSchedule: this.sourceSettings.reminderNotificationSchedule || []
      });
      this.initializeNotification(this.sourceSettings.notification || {});
      this.setSwitchValues(this.sourceSettings);
    }
    this.settingsForm.markAsPristine();
  }

  getTranslation(value: string) {
    if (value === 'Remote') {
      return this.translate.instant('common.RemoteService');
    }

    if (value === 'Rideshare' && this.translate.currentLang.split('-')[1] === 'MX') {
      return this.translate.instant('service.complimentaryTransportation');
    }

    return this.translate.instant(`common.${value}`);
  }

  navigateToDIS() {
    window.open(environment.dis, '_blank');
  }

  getIntegrationText() {
    return this.translate.instant('otherTexts.syncFromDis');
  }

  syncDealerDisDetails() {
    this.runSyncWithRetry(1);
  }

  showError() {
    this.syncInProgress = false;
    this.alertService.showAlert('otherTexts.somethingWentWrongPleaseTryAgain');
  }

  private runSyncWithRetry(attempt: number) {
    this.syncInProgress = true;
    this.settingsService.syncDealerDisDetails().subscribe(res => {
      if (res.message === 'Index Updated Successfully') {
        this.syncInProgress = false;
        this.setUpStore();
        this.alertService.showAlert('otherTexts.syncedSuccessfully', '', '', 5000, false);
        return;
      }

      this.handleSyncFailure(attempt);
    }, () => {
      this.handleSyncFailure(attempt);
    });
  }

  private handleSyncFailure(attempt: number) {
    this.syncInProgress = false;
    if (attempt >= 3) {
      this.alertService.showEscalationAlert();
      return;
    }

    this.subs.syncRetry = this.alertService.openRetryPopup(this.translate.instant('otherTexts.somethingWentWrongPleaseTryAgain'))
      .subscribe((shouldRetry) => {
        if (shouldRetry) {
          this.runSyncWithRetry(attempt + 1);
        }
      });
  }

  ngOnDestroy() {
    util.unsubscribeSubscriptions(this.subs);
  }
}
